import { usePushNotifications } from "@/lib/push";
import { toast } from "sonner";
import { Bell, BellOff, BellRing } from "lucide-react";

/**
 * Card shown on the parent dashboard prompting the user to enable push
 * notifications for fee reminders. Also usable in the admin ERP.
 */
export default function PushSubscribeCard({ testid = "push-card" }) {
  const { supported, permission, subscribed, loading, subscribe, unsubscribe, sendTest } = usePushNotifications();

  if (!supported) {
    return (
      <div className="border border-white/12 bg-[#131A2B] p-4 text-xs text-white/60 flex items-center gap-3" data-testid={`${testid}-unsupported`}>
        <BellOff size={16} className="brand-orange" />
        Push notifications aren't supported in this browser (open HTP Sports Hub in Chrome / Edge / Android).
      </div>
    );
  }

  const enable = async () => {
    const r = await subscribe();
    if (r.ok) toast.success("Notifications enabled — you'll receive fee reminders");
    else if (r.reason === "denied") toast.error("Notification permission was denied");
    else toast.error(r.error || "Could not enable notifications");
  };

  const disable = async () => {
    await unsubscribe();
    toast.info("Notifications turned off");
  };

  const test = async () => {
    try {
      await sendTest();
      toast.success("Test push sent");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to send test");
    }
  };

  return (
    <div className={`border ${subscribed ? "border-brand-blue/40 bg-brand-blue/5" : "border-white/12 bg-[#131A2B]"} p-5`} data-testid={testid}>
      <div className="flex items-start gap-4">
        <div className={`w-10 h-10 border ${subscribed ? "border-brand-blue text-brand-blue" : "border-white/20 text-white/50"} flex items-center justify-center shrink-0`}>
          {subscribed ? <BellRing size={18} /> : <Bell size={18} />}
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-mono text-[10px] uppercase tracking-widest brand-orange mb-1">// Fee reminders on your phone</div>
          <div className="font-bold text-sm">
            {subscribed ? "Push notifications are enabled" : "Never miss a fee reminder"}
          </div>
          <div className="text-xs text-white/60 mt-1">
            {subscribed
              ? "You'll receive alerts when invoices are due or overdue. Install this site to your home screen for the best experience."
              : "Get instant push alerts when a new invoice is issued or a payment is due. Works even when the site is closed."}
          </div>
          <div className="flex flex-wrap gap-2 mt-4">
            {!subscribed ? (
              <button
                onClick={enable} disabled={loading || permission === "denied"}
                data-testid={`${testid}-enable`}
                className="text-xs font-mono uppercase tracking-widest bg-brand-blue text-white px-4 py-2 hover:glow-blue disabled:opacity-40 flex items-center gap-2"
              >
                <BellRing size={12} /> Enable notifications
              </button>
            ) : (
              <>
                <button
                  onClick={test} disabled={loading}
                  data-testid={`${testid}-test`}
                  className="text-xs font-mono uppercase tracking-widest border border-brand-blue text-brand-blue px-4 py-2 hover:bg-brand-blue hover:text-white flex items-center gap-2"
                >
                  Send test push
                </button>
                <button
                  onClick={disable} disabled={loading}
                  data-testid={`${testid}-disable`}
                  className="text-xs font-mono uppercase tracking-widest border border-white/20 text-white/60 px-4 py-2 hover:text-white flex items-center gap-2"
                >
                  <BellOff size={12} /> Turn off
                </button>
              </>
            )}
            {permission === "denied" && (
              <div className="text-[10px] font-mono text-red-400">
                Permission was denied. Enable notifications in your browser settings.
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
